import { BaseApiClient } from '@/services/baseApiClient';
import {
  type MeterPopulate,
  ReplaceMeterFormField,
  ReportMeterFormField,
  type ViewMeters,
  type SerializedRevision,
} from '@enmon/commons/lib/enmon';
import { RequestError, type RequestOptions, uploadFile } from '@/utils/request';
import { getQuery } from '@/components/filter/helpers';
import type { BackendQuery } from '@/types/BackendQuery';
import { captureException } from '@sentry/react';
import type { Args, Results, MetersFilter } from '@/services/api/meters';

export class MetersApiClient extends BaseApiClient {
  constructor() {
    super('api/meters');
  }

  public create(payload: Args.CreateMeterArg): Promise<Results.CreateMeterResult> {
    return this.http.post('', payload);
  }

  public delete(meterId: Args.DeleteMeterArg): Promise<Results.DeleteMeterResult> {
    return this.http.delete(`${meterId}`);
  }

  public get<TView extends ViewMeters = ViewMeters.Default>(
    filter: Args.GetMetersArgs,
    populate: MeterPopulate[],
    view: TView,
    signal: RequestOptions['signal'],
  ): Promise<Results.GetMetersResult<TView>> {
    return this.http.get('', { query: getQuery({ ...filter, view, populate }), signal });
  }

  public getStats(filter: MetersFilter, signal: AbortSignal | undefined): Promise<Results.GetMetersStatsResult> {
    return this.http.get('stats', {
      query: getQuery(filter),
      signal,
    });
  }

  public getOne(
    meterId: Args.GetOneMeterArg,
    populate: MeterPopulate[],
    signal: RequestOptions['signal'],
  ): Promise<Results.GetOneMeterResult> {
    return this.http.get(`${meterId}`, { query: getQuery({ populate }), signal });
  }

  public update({ meterId, payload }: Args.UpdateMeterArgs): Promise<Results.UpdateMeterResult> {
    return this.http.patch(`${meterId}`, payload);
  }

  public getCounters(query: BackendQuery, signal: RequestOptions['signal']): Promise<Results.GetCountersResult> {
    return this.http.get('counter', { query: getQuery(query), signal });
  }

  public report({ meterId, message, attachments }: Args.FaultMeterReportArgs): Promise<Results.FaultMeterReportResult> {
    const formData = new FormData();

    for (const file of attachments) {
      formData.append(ReportMeterFormField.Attachments, file);
    }

    formData.append(ReportMeterFormField.Message, JSON.stringify({ message }));
    return uploadFile(`${this.http.urlPrefix}/${meterId}/report`, formData);
  }

  public batchReport({
    meterIds,
    message,
    attachments,
  }: Args.BatchFaultMeterReportArgs): Promise<Results.FaultMeterReportResult> {
    const formData = new FormData();

    for (const file of attachments) {
      formData.append(ReportMeterFormField.Attachments, file);
    }

    formData.append(ReportMeterFormField.Message, JSON.stringify({ message, meterIds }));
    return uploadFile(`${this.http.urlPrefix}/batch-report`, formData);
  }

  public replace({ meterId, payload, attachments }: Args.ReplaceMeterArgs): Promise<Results.ReplaceMeterResult> {
    const formData = new FormData();

    // Append form data
    formData.append(ReplaceMeterFormField.Data, JSON.stringify(payload));

    // Append attachments
    for (const [name, files] of Object.entries(attachments)) {
      for (const file of files) {
        formData.append(name, file);
      }
    }

    return uploadFile(`${this.http.urlPrefix}/${meterId}/replace`, formData);
  }

  public move({ establishmentId, meterId }: Args.MoveMeterArgs): Promise<Results.MoveMeterResult> {
    return this.http.post(`${meterId}/move`, { establishmentId });
  }

  public getRegisters(
    meterId: Args.GetMeterRegistersArg,
    signal: RequestOptions['signal'],
  ): Promise<Results.GetMeterRegistersResult> {
    return this.http.get(`${meterId}/registers`, { signal });
  }

  public createRegister({
    meterId,
    payload,
  }: Args.CreateMeterRegisterArgs): Promise<Results.CreateMeterRegisterResult> {
    return this.http.post(`${meterId}/registers`, payload);
  }

  public deleteRegister({
    meterId,
    registerId,
  }: Args.DeleteMeterRegisterArgs): Promise<Results.DeleteMeterRegisterResult> {
    return this.http.delete(`${meterId}/registers/${registerId}`);
  }

  public updateRegister({
    meterId,
    registerId,
    payload,
  }: Args.UpdateMeterRegisterArgs): Promise<Results.UpdateMeterRegisterResult> {
    return this.http.patch(`${meterId}/registers/${registerId}`, payload);
  }

  public async import({ importFile, attachments }: Args.ImportMeterArgs): Promise<Results.ImportMeterResult> {
    const formData = new FormData();

    formData.append('importFile', importFile);

    if (attachments) {
      for (const file of attachments) {
        formData.append('attachments', file);
      }
    }

    try {
      return await uploadFile(`${this.http.urlPrefix}/import`, formData, {
        signal: undefined,
      });
    } catch (e) {
      if (e instanceof RequestError && e.response.status === 412) {
        return await e.response.json<Results.ImportMeterResult>();
      }
      console.error(e);
      captureException(e);
      throw e;
    }
  }

  public getRevisions(
    { meterId, ...filter }: Args.GetRevisionsArg,
    signal: RequestOptions['signal'],
  ): Promise<Results.GetRevisionsResult> {
    return this.http.get(`${meterId}/revisions`, {
      query: getQuery(filter),
      signal,
    });
  }

  public revertRevision(meterId: Args.RevertRevisionArg): Promise<Results.RevertRevisionResult> {
    return this.http.put(`${meterId}/revisions/revert`, {});
  }

  public updateRevision({
    meterId,
    revisionId,
    payload,
  }: Args.UpdateRevisionArgs): Promise<Results.UpdateRevisionResult> {
    return this.http.patch<SerializedRevision>(`${meterId}/revisions/${revisionId}`, payload);
  }

  public deleteOneRevision({
    meterId,
    revisionId,
  }: Args.DeleteOneRevisionArgs): Promise<Results.DeleteOneRevisionResult> {
    return this.http.delete(`${meterId}/revisions/${revisionId}`);
  }

  public deleteRevisions(meterId: Args.DeleteRevisionsArg): Promise<Results.DeleteRevisionsResult> {
    return this.http.delete(`${meterId}/revisions`);
  }
}
